<template>
  <q-page class="course-detail-page">
    <!-- Loading state -->
    <div v-if="loading" class="course-detail-container">
      <!-- Hero skeleton -->
      <div class="hero-card">
        <div class="hero-bg"><div class="hero-glow" /></div>
        <div class="hero-content">
          <q-skeleton type="text" width="60px" dark style="margin-bottom: 16px;" />
          <div style="display: flex; gap: 8px; margin-bottom: 12px;">
            <q-skeleton type="rect" width="80px" height="20px" dark style="border-radius: 9999px;" />
            <q-skeleton type="rect" width="60px" height="20px" dark style="border-radius: 9999px;" />
          </div>
          <q-skeleton type="text" width="80%" dark style="font-size: 2rem;" />
          <q-skeleton type="text" width="90%" dark style="margin-top: 12px;" />
          <q-skeleton type="text" width="70%" dark />
          <div style="display: flex; gap: 24px; margin-top: 24px; padding-top: 24px; border-top: 1px solid rgba(255,255,255,0.05);">
            <div>
              <q-skeleton type="text" width="40px" dark style="font-size: 1.5rem;" />
              <q-skeleton type="text" width="60px" dark />
            </div>
            <div>
              <q-skeleton type="text" width="40px" dark style="font-size: 1.5rem;" />
              <q-skeleton type="text" width="70px" dark />
            </div>
          </div>
        </div>
      </div>
      <!-- CTA skeleton -->
      <q-skeleton type="rect" height="80px" dark style="border-radius: 16px;" />
      <!-- Curriculum skeleton -->
      <div>
        <q-skeleton type="text" width="200px" dark style="font-size: 1.25rem; margin-bottom: 16px;" />
        <q-skeleton v-for="n in 3" :key="n" type="rect" height="120px" dark style="border-radius: 16px; margin-bottom: 12px;" />
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="course-detail-container">
      <div class="detail-empty-state">
        <div class="detail-empty-icon-wrap">
          <q-icon name="error_outline" size="32px" style="color: var(--oa-danger, var(--color-terracotta));" />
        </div>
        <h2 class="detail-empty-title">{{ notFound ? 'Curso no encontrado' : 'Error al cargar el curso' }}</h2>
        <p class="detail-empty-text">{{ notFound ? 'El curso que buscas no existe o no esta disponible.' : error }}</p>
        <q-btn
          no-caps
          unelevated
          :label="notFound ? 'Volver al catalogo' : 'Reintentar'"
          class="detail-retry-btn"
          @click="notFound ? $router.push('/courses') : fetchData()"
        />
      </div>
    </div>

    <!-- Course loaded -->
    <div v-else-if="course" class="course-detail-container">
      <!-- Hero section -->
      <div class="hero-card">
        <div class="hero-bg"><div class="hero-glow" /></div>
        <div class="hero-content">
          <!-- Back link -->
          <router-link to="/courses" class="hero-back-link">
            &larr; Catalogo
          </router-link>

          <!-- Badges -->
          <div class="hero-badges">
            <span class="badge-gold">{{ course.careerTrack }}</span>
            <span v-if="course.requiredLevel" class="badge-outline">
              Nivel {{ course.requiredLevel }}
            </span>
          </div>

          <!-- Title -->
          <h1 class="hero-title">{{ course.title }}</h1>

          <!-- Description -->
          <p v-if="course.description" class="hero-description">
            {{ course.description }}
          </p>

          <!-- Stats -->
          <div class="hero-stats">
            <div class="hero-stat">
              <p class="hero-stat-value">{{ course.totalUnits }}</p>
              <p class="hero-stat-label">Unidades</p>
            </div>
            <div class="hero-stat-divider" />
            <div class="hero-stat">
              <p class="hero-stat-value">{{ computedTotalLessons }}</p>
              <p class="hero-stat-label">Lecciones</p>
            </div>
            <template v-if="course.estimatedHours">
              <div class="hero-stat-divider" />
              <div class="hero-stat">
                <p class="hero-stat-value">~{{ course.estimatedHours }}</p>
                <p class="hero-stat-label">Horas</p>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- CTA card -->
      <div class="cta-card">
        <div class="cta-info">
          <div v-if="course.isFree" class="cta-price-free">
            <span class="cta-price-value-free">Gratis</span>
            <span class="badge-free-sm">Acceso completo</span>
          </div>
          <div v-else class="cta-price-paid">
            <span class="cta-price-value">
              ${{ course.priceArs ? Number(course.priceArs).toLocaleString('es-AR') : '---' }}
            </span>
            <span class="cta-price-currency">ARS</span>
          </div>
          <p class="cta-subtitle">Acceso de por vida a todo el contenido</p>
        </div>
        <div class="cta-action">
          <router-link v-if="isEnrolled" :to="`/learn/${course.id}`" class="cta-link">
            <q-btn
              no-caps
              unelevated
              label="Ir al curso"
              icon-right="arrow_forward"
              class="cta-btn-gold"
            />
          </router-link>
          <q-btn
            v-else
            no-caps
            unelevated
            label="Inscribirme"
            icon-right="arrow_forward"
            :loading="enrolling"
            class="cta-btn-gold"
            @click="handleEnroll"
          />
        </div>
      </div>

      <!-- Curriculum section -->
      <div class="curriculum-section">
        <h2 class="curriculum-title">Contenido del curso</h2>

        <div class="curriculum-list">
          <div
            v-for="(unit, i) in course.units"
            :key="unit.id"
            class="unit-card"
          >
            <!-- Unit header -->
            <div class="unit-header">
              <div class="unit-number-badge">{{ i + 1 }}</div>
              <div class="unit-header-info">
                <h3 class="unit-header-title">
                  {{ unit.displayNumber || `UD${i + 1}` }}: {{ unit.title }}
                </h3>
                <p v-if="unit.description" class="unit-header-desc">
                  {{ unit.description }}
                </p>
              </div>
              <span class="unit-header-meta">
                {{ unit.lessons.length }} lecciones{{ getUnitMinutes(unit) > 0 ? ` - ~${getUnitMinutes(unit)} min` : '' }}
              </span>
            </div>

            <!-- Lessons list -->
            <div class="unit-lessons">
              <div
                v-for="(lesson, j) in unit.lessons"
                :key="lesson.id"
                class="lesson-row"
                :class="{ 'lesson-row-alt': j % 2 === 1 }"
              >
                <!-- Lesson number -->
                <span class="lesson-number">
                  {{ lesson.contentType === 'quiz' ? '?' : j + 1 }}
                </span>

                <!-- Content type icon -->
                <span
                  class="lesson-type-icon"
                  :class="{
                    'lesson-type-video': lesson.contentType === 'video',
                    'lesson-type-quiz': lesson.contentType === 'quiz',
                    'lesson-type-text': lesson.contentType !== 'video' && lesson.contentType !== 'quiz',
                  }"
                >
                  {{ lesson.contentType === 'video' ? '\u25B6' : lesson.contentType === 'quiz' ? '\u2753' : '\u2630' }}
                </span>

                <!-- Title -->
                <span class="lesson-title">{{ lesson.title }}</span>

                <!-- Minutes -->
                <span v-if="lesson.estimatedMinutes" class="lesson-minutes">
                  {{ lesson.estimatedMinutes }} min
                </span>

                <!-- Free badge -->
                <span v-if="lesson.isFree" class="badge-free-sm">Gratis</span>

                <!-- XP -->
                <span class="lesson-xp">+{{ lesson.xpReward }} AURA</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import { api } from 'boot/axios';
import { useAuthStore } from 'stores/useAuthStore';

interface Lesson {
  id: string;
  title: string;
  contentType: string;
  orderIndex: number;
  estimatedMinutes: number | null;
  xpReward: number;
  isFree: boolean;
}

interface Unit {
  id: string;
  title: string;
  description: string | null;
  orderIndex: number;
  displayNumber: string | null;
  track: string;
  lessons: Lesson[];
}

interface CourseDetail {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  shortDescription: string | null;
  coverImageUrl: string | null;
  status: string;
  priceArs: string | null;
  priceUsd: string | null;
  isFree: boolean;
  requiredLevel: string;
  careerTrack: string;
  totalUnits: number;
  totalLessons: number;
  estimatedHours: number | null;
  createdAt: string;
  units: Unit[];
}

interface Enrollment {
  id: string;
  userId: string;
  courseId: string;
  status: string;
  progressPct: number;
  enrolledAt: string;
}

const route = useRoute();
const router = useRouter();
const $q = useQuasar();
const authStore = useAuthStore();

const course = ref<CourseDetail | null>(null);
const isEnrolled = ref(false);
const loading = ref(true);
const error = ref<string | null>(null);
const notFound = ref(false);
const enrolling = ref(false);

const computedTotalLessons = computed(() => {
  if (!course.value) return 0;
  return course.value.units.reduce((sum, u) => sum + u.lessons.length, 0);
});

function getUnitMinutes(unit: Unit): number {
  return unit.lessons.reduce((sum, l) => sum + (l.estimatedMinutes ?? 0), 0);
}

async function fetchData() {
  loading.value = true;
  error.value = null;
  notFound.value = false;

  try {
    const slug = route.params.slug as string;
    const requests: Promise<unknown>[] = [
      api.get<CourseDetail>(`/courses/${slug}`),
    ];

    if (authStore.isAuthenticated) {
      requests.push(api.get<Enrollment[]>('/learn/enrollments'));
    }

    const results = await Promise.all(requests);
    course.value = (results[0] as { data: CourseDetail }).data;

    if (results[1] && course.value) {
      const enrollmentList = (results[1] as { data: Enrollment[] }).data;
      isEnrolled.value = enrollmentList.some(
        (e) => e.courseId === course.value!.id
      );
    }
  } catch (err: unknown) {
    if (
      err &&
      typeof err === 'object' &&
      'response' in err &&
      (err as { response?: { status?: number } }).response?.status === 404
    ) {
      notFound.value = true;
      error.value = 'Curso no encontrado';
    } else {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      error.value = message;
    }
  } finally {
    loading.value = false;
  }
}

async function handleEnroll() {
  if (!course.value) return;

  enrolling.value = true;
  try {
    await api.post('/courses/enroll', { courseId: course.value.id });
    isEnrolled.value = true;
    $q.notify({
      type: 'positive',
      message: 'Inscripcion exitosa',
      caption: `Ya podes acceder a ${course.value.title}`,
      position: 'top',
      timeout: 3000,
    });
  } catch (err: unknown) {
    if (
      err &&
      typeof err === 'object' &&
      'response' in err &&
      (err as { response?: { status?: number } }).response?.status === 409
    ) {
      isEnrolled.value = true;
      $q.notify({
        type: 'info',
        message: 'Ya estas inscripto',
        caption: 'Ya tenes acceso a este curso',
        position: 'top',
        timeout: 3000,
      });
    } else {
      $q.notify({
        type: 'negative',
        message: 'Error al inscribirte',
        caption: 'Intenta de nuevo mas tarde',
        position: 'top',
        timeout: 3000,
      });
    }
  } finally {
    enrolling.value = false;
  }
}

onMounted(fetchData);
</script>

<style lang="scss" scoped>
.course-detail-page {
  display: flex;
  justify-content: center;
  padding: 24px 16px;
}

.course-detail-container {
  max-width: 960px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

// --- Hero Card ---
.hero-card {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, var(--oa-bg-2, var(--color-sandy-beige)) 0%, var(--oa-bg-1, var(--color-warm-linen)) 100%);
  border: 1px solid color-mix(in srgb, var(--oa-border, rgba(61, 55, 50, 0.12)) 50%, transparent);
  border-radius: 16px;
}

.hero-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.hero-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at top right, var(--oa-gold-full, var(--color-terracotta)) 0%, transparent 50%);
  opacity: 0.1;
}

.hero-content {
  position: relative;
  padding: 32px;
}

@media (min-width: 768px) {
  .hero-content {
    padding: 48px;
  }
}

// --- Back link ---
.hero-back-link {
  display: inline-block;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
  color: color-mix(in srgb, var(--oa-text-primary, var(--color-deep-charcoal)) 40%, transparent);
  text-decoration: none;
  transition: color 0.2s;
  margin-bottom: 16px;

  &:hover {
    color: var(--oa-gold-full, var(--color-terracotta));
  }
}

// --- Badges ---
.hero-badges {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.badge-gold {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
  background: linear-gradient(135deg, var(--oa-gold-dim, rgba(184, 155, 94, 0.55)), var(--oa-gold-full, var(--color-terracotta)));
  color: var(--oa-bg-0, var(--color-marble-cream));
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.badge-outline {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 500;
  border: 1px solid color-mix(in srgb, var(--oa-text-primary, var(--color-deep-charcoal)) 20%, transparent);
  color: color-mix(in srgb, var(--oa-text-primary, var(--color-deep-charcoal)) 60%, transparent);
}

// --- Hero Title ---
.hero-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--oa-text-primary, var(--color-deep-charcoal));
  margin: 0;
  line-height: 1.2;
}

@media (min-width: 768px) {
  .hero-title {
    font-size: 2.25rem;
  }
}

// --- Hero Description ---
.hero-description {
  color: color-mix(in srgb, var(--oa-text-primary, var(--color-deep-charcoal)) 60%, transparent);
  margin: 16px 0 0;
  font-size: 1rem;
  line-height: 1.6;
  max-width: 640px;
}

@media (min-width: 768px) {
  .hero-description {
    font-size: 1.125rem;
  }
}

// --- Hero Stats ---
.hero-stats {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid color-mix(in srgb, var(--oa-text-primary, var(--color-deep-charcoal)) 10%, transparent);
}

.hero-stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--oa-text-primary, var(--color-deep-charcoal));
  margin: 0;
}

.hero-stat-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: color-mix(in srgb, var(--oa-text-primary, var(--color-deep-charcoal)) 40%, transparent);
  margin: 2px 0 0;
}

.hero-stat-divider {
  width: 1px;
  height: 40px;
  background: color-mix(in srgb, var(--oa-border, rgba(61, 55, 50, 0.12)) 30%, transparent);
}

// --- CTA Card ---
.cta-card {
  background: var(--oa-bg-2, var(--color-sandy-beige));
  border: 1px solid color-mix(in srgb, var(--oa-border, rgba(61, 55, 50, 0.12)) 50%, transparent);
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: stretch;
}

@media (min-width: 640px) {
  .cta-card {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 24px 32px;
  }
}

.cta-info {
  flex: 1;
}

.cta-price-free {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cta-price-value-free {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--oa-success, var(--oa-success));
}

.badge-free-sm {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 0.65rem;
  font-weight: 600;
  background: color-mix(in srgb, var(--oa-success, var(--oa-success)) 15%, transparent);
  color: var(--oa-success, var(--oa-success));
}

.cta-price-paid {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.cta-price-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--oa-text-primary, var(--color-deep-charcoal));
}

.cta-price-currency {
  font-size: 0.875rem;
  color: color-mix(in srgb, var(--oa-text-primary, var(--color-deep-charcoal)) 40%, transparent);
}

.cta-subtitle {
  font-size: 0.8125rem;
  color: color-mix(in srgb, var(--oa-text-primary, var(--color-deep-charcoal)) 50%, transparent);
  margin: 4px 0 0;
}

.cta-action {
  flex-shrink: 0;
}

.cta-link {
  text-decoration: none;
}

.cta-btn-gold {
  background: var(--oa-gold-full, var(--color-terracotta)) !important;
  color: var(--color-marble-cream) !important;
  font-weight: 600;
  font-size: 0.9375rem;
  border-radius: 12px;
  padding: 10px 32px;
  box-shadow: 0 0 16px rgba(232, 184, 75, 0.1);
}

// --- Curriculum Section ---
.curriculum-section {
  margin-top: 8px;
}

.curriculum-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--oa-text-primary, var(--color-deep-charcoal));
  margin: 0 0 20px;
}

.curriculum-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

// --- Unit Card ---
.unit-card {
  background: var(--oa-bg-2, var(--color-sandy-beige));
  border: 1px solid color-mix(in srgb, var(--oa-border, rgba(61, 55, 50, 0.12)) 50%, transparent);
  border-radius: 16px;
  overflow: hidden;
}

.unit-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid color-mix(in srgb, var(--oa-border, rgba(61, 55, 50, 0.12)) 30%, transparent);
}

.unit-number-badge {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--oa-gold-full, var(--color-terracotta)) 10%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--oa-gold-full, var(--color-terracotta));
  flex-shrink: 0;
}

.unit-header-info {
  flex: 1;
  min-width: 0;
}

.unit-header-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--oa-text-primary, var(--color-deep-charcoal));
  margin: 0;
}

.unit-header-desc {
  font-size: 0.8125rem;
  color: color-mix(in srgb, var(--oa-text-primary, var(--color-deep-charcoal)) 50%, transparent);
  margin: 2px 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.unit-header-meta {
  font-size: 0.75rem;
  color: color-mix(in srgb, var(--oa-text-primary, var(--color-deep-charcoal)) 30%, transparent);
  flex-shrink: 0;
  white-space: nowrap;
}

// --- Lesson Rows ---
.unit-lessons {
  // no extra padding — rows are full-width
}

.lesson-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 24px;
  font-size: 0.8125rem;
  border-bottom: 1px solid color-mix(in srgb, var(--oa-border, rgba(61, 55, 50, 0.12)) 20%, transparent);

  &:last-child {
    border-bottom: none;
  }
}

.lesson-row-alt {
  background: color-mix(in srgb, var(--oa-bg-3, var(--color-warm-stone)) 3%, transparent);
}

.lesson-number {
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  background: color-mix(in srgb, var(--oa-bg-3, var(--color-warm-stone)) 15%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  color: color-mix(in srgb, var(--oa-text-primary, var(--color-deep-charcoal)) 30%, transparent);
  flex-shrink: 0;
}

.lesson-type-icon {
  font-size: 0.75rem;
  width: 16px;
  flex-shrink: 0;
  text-align: center;
}

.lesson-type-text {
  color: color-mix(in srgb, var(--oa-text-primary, var(--color-deep-charcoal)) 20%, transparent);
}

.lesson-type-video {
  color: color-mix(in srgb, var(--oa-gold-full, var(--color-terracotta)) 40%, transparent);
}

.lesson-type-quiz {
  color: color-mix(in srgb, #e8c84b 40%, transparent);
}

.lesson-title {
  flex: 1;
  color: color-mix(in srgb, var(--oa-text-primary, var(--color-deep-charcoal)) 60%, transparent);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lesson-minutes {
  font-size: 0.75rem;
  color: color-mix(in srgb, var(--oa-text-primary, var(--color-deep-charcoal)) 20%, transparent);
  flex-shrink: 0;
}

.lesson-xp {
  font-size: 0.75rem;
  color: color-mix(in srgb, var(--oa-text-primary, var(--color-deep-charcoal)) 20%, transparent);
  flex-shrink: 0;
}

// --- Empty / Error State ---
.detail-empty-state {
  text-align: center;
  padding: 48px 16px;
}

.detail-empty-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--oa-gold-full, var(--color-terracotta)) 12%, transparent);
}

.detail-empty-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--oa-text-primary, var(--color-deep-charcoal));
  margin: 0 0 8px;
}

.detail-empty-text {
  font-size: 0.875rem;
  color: var(--oa-text-muted, var(--color-olive-stone));
  margin: 0 0 24px;
}

.detail-retry-btn {
  background: var(--oa-gold-full, var(--color-terracotta)) !important;
  color: var(--color-marble-cream) !important;
  font-weight: 600;
  border-radius: 12px;
}

// --- Responsive adjustments ---
@media (max-width: 640px) {
  .hero-content {
    padding: 24px;
  }

  .hero-title {
    font-size: 1.5rem;
  }

  .hero-stats {
    gap: 16px;
  }

  .hero-stat-divider {
    display: none;
  }

  .unit-header {
    flex-wrap: wrap;
    gap: 8px;
  }

  .unit-header-meta {
    width: 100%;
    padding-left: 56px;
  }

  .lesson-row {
    padding: 12px 16px;
    gap: 8px;
  }

  .lesson-xp {
    display: none;
  }
}
</style>
